// ABOUTME: Video player component with engagement tracking and playback position saving
// ABOUTME: Supports YouTube, Vimeo, and S3-hosted videos with unified interface

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Chip,
  Paper,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
  Divider,
  Card,
  CardContent,
  CardActionArea,
  CardMedia,
  LinearProgress,
} from '@mui/material';

import { Home, Visibility, PlayCircle } from '@mui/icons-material';
import axios from 'axios';
import { getKBVideo, updateVideoEngagement, getRelatedVideos } from '../../services/api';
import { KBVideoDto, UpdateVideoEngagementDto } from '../../types/knowledgeBase';

interface VideoPlayerProps {
  videoId?: number; // If provided directly as prop
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ videoId: propVideoId }) => {
  const { id: paramId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const videoId = propVideoId || (paramId ? parseInt(paramId) : null);

  const [video, setVideo] = useState<KBVideoDto | null>(null);
  const [relatedVideos, setRelatedVideos] = useState<KBVideoDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPosition, setCurrentPosition] = useState<number>(0);
  const [isCompleted, setIsCompleted] = useState(false);

  const trackingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoId) {
      loadVideo(videoId);
    }

    return () => {
      // Cleanup: Save final position when component unmounts
      if (trackingIntervalRef.current) {
        clearInterval(trackingIntervalRef.current);
      }
      saveEngagement();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadVideo and saveEngagement intentionally omitted; they are stable and adding them would cause extra fetches
  }, [videoId]);

  const loadVideo = async (id: number) => {
    try {
      setLoading(true);
      setError(null);
      const videoData = await getKBVideo(id);
      setVideo(videoData);

      // Restore last watched position if available
      // Note: This would come from engagement data in a real implementation
      // For now, we'll start from the beginning

      // Load related videos
      await loadRelatedVideos(videoData);
    } catch (err: unknown) {
      console.error('Failed to load video:', err);
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        setError('Video not found or is not available.');
      } else {
        setError('Failed to load video. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedVideos = async (currentVideo: KBVideoDto) => {
    try {
      const related = await getRelatedVideos(currentVideo.id);
      setRelatedVideos(related);
    } catch (err) {
      console.error('Failed to load related videos:', err);
      // Non-critical, don't show error to user
    }
  };

  const saveEngagement = async () => {
    if (!video || !videoRef.current) return;

    const position = Math.floor(videoRef.current.currentTime);
    const duration = videoRef.current.duration;
    const completed = duration > 0 && position / duration > 0.9; // Consider completed if >90% watched

    try {
      const engagementDto: UpdateVideoEngagementDto = {
        videoId: video.id,
        lastPosition: position,
        isCompleted: completed,
      };

      await updateVideoEngagement(engagementDto);
      setIsCompleted(completed);
    } catch (err) {
      console.error('Failed to save video engagement:', err);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentPosition(videoRef.current.currentTime);
    }
  };

  const handleVideoPlay = () => {
    // Start periodic engagement tracking (every 10 seconds)
    if (trackingIntervalRef.current) {
      clearInterval(trackingIntervalRef.current);
    }
    trackingIntervalRef.current = setInterval(() => {
      saveEngagement();
    }, 10000);
  };

  const handleVideoPause = () => {
    // Save engagement when paused
    saveEngagement();
    if (trackingIntervalRef.current) {
      clearInterval(trackingIntervalRef.current);
    }
  };

  const handleVideoEnded = () => {
    // Mark as completed
    setIsCompleted(true);
    saveEngagement();
    if (trackingIntervalRef.current) {
      clearInterval(trackingIntervalRef.current);
    }
  };

  const handleRelatedVideoClick = (relatedVideoId: number) => {
    navigate(`/knowledge-base/video/${relatedVideoId}`);
  };

  const renderVideoPlayer = () => {
    if (!video) return null;

    const playbackUrl = video.playbackUrl;

    // For YouTube/Vimeo, we would use iframe embeds
    // For S3, we use HTML5 video element
    const isYouTube = playbackUrl.includes('youtube.com') || playbackUrl.includes('youtu.be');
    const isVimeo = playbackUrl.includes('vimeo.com');

    if (isYouTube) {
      // Extract YouTube video ID and create embed URL
      const youtubeId = playbackUrl.split('v=')[1]?.split('&')[0] || playbackUrl.split('/').pop();
      return (
        <Box
          component="iframe"
          src={`https://www.youtube.com/embed/${youtubeId}?enablejsapi=1&rel=0&modestbranding=1&showinfo=0`}
          width="100%"
          height="500px"
          sx={{ border: 'none', borderRadius: 1 }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      );
    }

    if (isVimeo) {
      const vimeoId = playbackUrl.split('/').pop();
      return (
        <Box
          component="iframe"
          src={`https://player.vimeo.com/video/${vimeoId}`}
          width="100%"
          height="500px"
          sx={{ border: 'none', borderRadius: 1 }}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
        />
      );
    }

    // Default: HTML5 video for S3 or direct URLs
    return (
      <Box
        component="video"
        ref={videoRef}
        width="100%"
        height="500px"
        controls
        onPlay={handleVideoPlay}
        onPause={handleVideoPause}
        onEnded={handleVideoEnded}
        onTimeUpdate={handleTimeUpdate}
        sx={{ borderRadius: 1 / 3 }} // theme.shape.borderRadius(12) * (1/3) = 4px
      >
        <source src={playbackUrl} type="video/mp4" />
        Your browser does not support the video tag.
      </Box>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !video) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Video not found'}</Alert>
        <Box mt={2}>
          <Link component="button" onClick={() => navigate('/knowledge-base')}>
            ← Back to Knowledge Base
          </Link>
        </Box>
      </Container>
    );
  }

  const progressPercentage =
    video.durationSeconds && currentPosition
      ? (currentPosition / video.durationSeconds) * 100
      : 0;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Box mb={3}>
        <Breadcrumbs>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/knowledge-base')}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="small" />
            Knowledge Base
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/knowledge-base?type=videos')}
            sx={{ cursor: 'pointer' }}
          >
            Videos
          </Link>
          <Typography variant="body2" color="text.primary">
            {video.title}
          </Typography>
        </Breadcrumbs>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {/* Main Video Player */}
        <Box sx={{ width: { xs: '100%', md: '66.666%' } }}>
          <Paper sx={{ mb: 2 }}>{renderVideoPlayer()}</Paper>

          {/* Video Info */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              {video.title}
            </Typography>

            {video.description && (
              <Typography variant="body1" color="text.secondary" paragraph>
                {video.description}
              </Typography>
            )}

            {/* Metadata */}
            <Box display="flex" gap={3} alignItems="center" flexWrap="wrap" mb={2}>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Visibility fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {video.viewCount} views
                </Typography>
              </Box>

              <Chip label={video.audienceTag} size="small" color="primary" />

              {video.durationSeconds && (
                <Typography variant="body2" color="text.secondary">
                  Duration: {Math.floor(video.durationSeconds / 60)}:
                  {(video.durationSeconds % 60).toString().padStart(2, '0')}
                </Typography>
              )}

              {isCompleted && <Chip label="Completed" size="small" color="success" />}
            </Box>

            {/* Progress Bar */}
            {video.durationSeconds && currentPosition > 0 && (
              <Box mb={2}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Progress: {Math.floor(progressPercentage)}%
                </Typography>
                <LinearProgress variant="determinate" value={progressPercentage} />
              </Box>
            )}

            {/* Tags */}
            {video.tags.length > 0 && (
              <Box display="flex" gap={1} flexWrap="wrap">
                {video.tags.map((tag) => (
                  <Chip key={tag.id} label={tag.name} size="small" />
                ))}
              </Box>
            )}
          </Paper>
        </Box>

        {/* Related Videos Sidebar */}
        <Box sx={{ width: { xs: '100%', md: '33.333%' } }}>
          {relatedVideos.length > 0 && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Related Videos
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box display="flex" flexDirection="column" gap={2}>
                {relatedVideos.map((related) => (
                  <Card key={related.id} sx={{ cursor: 'pointer' }}>
                    <CardActionArea onClick={() => handleRelatedVideoClick(related.id)}>
                      {related.thumbnailUrl && (
                        <CardMedia
                          component="img"
                          height="100"
                          image={related.thumbnailUrl}
                          alt={related.title}
                        />
                      )}
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                          {related.title}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1}>
                          <PlayCircle fontSize="small" color="action" />
                          <Typography variant="caption" color="text.secondary">
                            {related.viewCount} views
                          </Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                ))}
              </Box>
            </Paper>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default VideoPlayer;
